import { keyBy } from 'lodash';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useMemo } from 'react';
import { usePrevious } from 'react-use';
import {
  LuArrowUpRight,
  LuBox,
  LuBoxes,
  LuFileText,
  LuLink,
  LuPackagePlus,
  LuRefreshCcw,
  LuTrendingUp,
  LuTruck,
  LuUsers,
  LuWarehouse,
} from 'react-icons/lu';

import CashBalancePage from '@features/cashBalance/CashBalancePage';
import CustomersPage from '@features/finance/CustomersPage';
import InvoicesPage from '@features/finance/InvoicesPage';
import PaymentLinksPage from '@features/finance/PaymentLinksPage';
import PayoutsPage from '@features/finance/PayoutsPage';
import SuppliersPage from '@features/finance/SuppliersPage';
import WarehousePage from '@features/comingSoon/WarehousePage';
import CreatePage from '@features/create/CreatePage';
import ExploreCommoditiesPage from '@features/explore/ExploreCommoditiesPage';
import HedgePositionsPage from '@features/positions/hedgePositions/HedgePositionsPage';
import MyMerchandisePage from '@features/positions/merchandise/MyMerchandisePage';
import MyNeedListPage from '@features/positions/needList/MyNeedListPage';
import PositionsHomePage from '@features/positions/PositionsHomePage';
import PredictionsPage from '@features/positions/predictions/PredictionsPage';
import MySupplyCommitmentsPage from '@features/positions/supplyCommitments/MySupplyCommitmentsPage';
import PredictionMarketPage from '@features/prediction-markets/PredictionMarketPage';
import SupplyChainsPage from '@features/supply/SupplyChainsPage';
import Workflows from '@features/workflows/Workflows';
import { DEFAULT_PERSONA_MODE, PersonaMode } from '@features/navigation/personaMode';
import { useActivePersonaMode } from 'hooks/useActivePersonaMode';
import { useOrgSlug } from 'hooks/useOrgSlug';
import { useStores } from 'stores/rootStore';
import {
  CreateIcon,
  DashboardIcon,
  ExploreIcon,
  GlobeStandIcon,
  HelpIcon,
  PageIcon,
  WalletIcon,
} from 'vectors';

export type NavigationEntity = {
  id: string;
  name: string;
  navigationTitle?: React.ReactNode;
  relativePath: string;
  absolutePath?: string;
  icon?: React.ReactNode;
  children: NavigationEntity[];
  component?: React.FC | React.ComponentClass;
  enableExplanation?: boolean;
  hide?: boolean;
};

// Home dashboard sub-items. Each mode surfaces only the subset that fits its persona.
const myHedgesEntity: NavigationEntity = {
  id: 'hedges',
  name: 'My hedges',
  relativePath: '/hedges',
  icon: <HelpIcon />,
  children: [],
  component: HedgePositionsPage,
  enableExplanation: true,
};

const myPredictionsEntity: NavigationEntity = {
  id: 'predictions',
  name: 'My predictions',
  relativePath: '/predictions',
  icon: <LuTrendingUp size="18px" />,
  children: [],
  component: PredictionsPage,
  enableExplanation: true,
};

const myNeedListEntity: NavigationEntity = {
  id: 'need-list',
  name: 'My need list',
  relativePath: '/need-list',
  icon: <LuBox size="18px" />,
  children: [],
  component: MyNeedListPage,
};

const myMerchandiseEntity: NavigationEntity = {
  id: 'merchandise',
  name: 'My merchandise',
  relativePath: '/merchandise',
  icon: <LuBoxes size="18px" />,
  children: [],
  component: MyMerchandisePage,
};

const mySupplyCommitmentsEntity: NavigationEntity = {
  id: 'supply-commitments',
  name: 'My supply commitments',
  relativePath: '/supply-commitments',
  icon: <LuPackagePlus size="18px" />,
  children: [],
  component: MySupplyCommitmentsPage,
};

// Home is folded into every mode, but its sub-items vary by persona.
const makeHomeEntity = (children: NavigationEntity[]): NavigationEntity => ({
  id: 'positions',
  name: 'Home',
  icon: <DashboardIcon />,
  relativePath: '/positions',
  children,
  component: PositionsHomePage,
});

const cashBalanceEntity: NavigationEntity = {
  id: 'cash-balance',
  name: 'Cash balance',
  icon: <WalletIcon />,
  relativePath: '/cash-balance',
  children: [],
  component: CashBalancePage,
  enableExplanation: true,
};

const predictEntity: NavigationEntity = {
  id: 'prediction-markets',
  name: 'Predict',
  relativePath: '/prediction-markets',
  icon: <GlobeStandIcon />,
  children: [],
  component: PredictionMarketPage,
};

const hedgeEntity: NavigationEntity = {
  id: 'explore',
  name: 'Hedge',
  icon: <HelpIcon />,
  relativePath: '/explore',
  component: ExploreCommoditiesPage,
  children: [],
};

// `/supply` (SupplyChainsPage) is the demand-signal surface, shared by Explorer and Supplier.
const demandSignalsEntity: NavigationEntity = {
  id: 'supply',
  name: 'Explore demand signals',
  relativePath: '/supply',
  icon: <ExploreIcon />,
  children: [],
  component: SupplyChainsPage,
};

// `/procure` (CreatePage) is the operator's single create surface; it will grow
// procurement forms for freeform needs and commodities.
const procureEntity: NavigationEntity = {
  id: 'procure',
  name: 'Procure',
  icon: <CreateIcon />,
  relativePath: '/procure',
  children: [],
  component: CreatePage,
  enableExplanation: true,
};

const workflowsEntity: NavigationEntity = {
  id: 'workflows',
  name: 'Workflows',
  relativePath: '/workflows',
  icon: <LuRefreshCcw size="18px" />,
  children: [],
  component: Workflows,
};

const paymentLinksEntity: NavigationEntity = {
  id: 'payment-links',
  name: 'Payment links',
  relativePath: '/payment-links',
  icon: <LuLink size="18px" />,
  children: [],
  component: PaymentLinksPage,
  enableExplanation: true,
};

const payoutsEntity: NavigationEntity = {
  id: 'payouts',
  name: 'Payouts',
  relativePath: '/payouts',
  icon: <LuArrowUpRight size="18px" />,
  children: [],
  component: PayoutsPage,
};

const invoicesEntity: NavigationEntity = {
  id: 'invoices',
  name: 'Invoices',
  relativePath: '/invoices',
  icon: <LuFileText size="18px" />,
  children: [],
  component: InvoicesPage,
};

const customersEntity: NavigationEntity = {
  id: 'customers',
  name: 'Customers',
  relativePath: '/customers',
  icon: <LuUsers size="18px" />,
  children: [],
  component: CustomersPage,
};

const suppliersEntity: NavigationEntity = {
  id: 'suppliers',
  name: 'Suppliers',
  relativePath: '/suppliers',
  icon: <LuTruck size="18px" />,
  children: [],
  component: SuppliersPage,
};

const warehouseEntity: NavigationEntity = {
  id: 'warehouse',
  name: 'Find warehouse',
  icon: <LuWarehouse size="18px" />,
  relativePath: '/warehouse',
  children: [],
  component: WarehousePage,
};

// Built lazily (see getSidebarRoots): these objects read page-component bindings
// (e.g. PositionsHomePage) as values. PositionsHomePage → Redirections → this module
// forms an import cycle, so reading those bindings at module-evaluation time risks a
// temporal-dead-zone crash in the production bundle. Deferring construction to the
// first hook call sidesteps the cycle.
let cachedSidebarRoots:
  | {
      personaModeNavigationRoots: Record<PersonaMode, NavigationEntity>;
      publicSidebarNavigationRoot: NavigationEntity;
    }
  | undefined;

const getSidebarRoots = () => {
  if (cachedSidebarRoots != null) {
    return cachedSidebarRoots;
  }

  const personaModeChildren: Record<PersonaMode, NavigationEntity[]> = {
    [PersonaMode.Analyst]: [
      makeHomeEntity([myHedgesEntity, myPredictionsEntity]),
      hedgeEntity,
      predictEntity,
      cashBalanceEntity,
    ],
    [PersonaMode.Operator]: [
      makeHomeEntity([myNeedListEntity]),
      procureEntity,
      warehouseEntity,
      workflowsEntity,
      cashBalanceEntity,
    ],
    [PersonaMode.Supplier]: [
      makeHomeEntity([myMerchandiseEntity, mySupplyCommitmentsEntity]),
      demandSignalsEntity,
      warehouseEntity,
      workflowsEntity,
      cashBalanceEntity,
    ],
    [PersonaMode.Finance]: [
      makeHomeEntity([]),
      invoicesEntity,
      paymentLinksEntity,
      customersEntity,
      suppliersEntity,
      payoutsEntity,
      workflowsEntity,
      cashBalanceEntity,
    ],
  };

  const personaModeNavigationRoots: Record<PersonaMode, NavigationEntity> = Object.fromEntries(
    Object.entries(personaModeChildren).map(([mode, children]) => [
      mode,
      { id: 'root', name: 'Root', relativePath: '/', children },
    ]),
  ) as Record<PersonaMode, NavigationEntity>;

  const publicSidebarNavigationRoot: NavigationEntity = {
    id: 'root',
    name: 'Root',
    relativePath: '/',
    children: [
      {
        id: 'positions',
        name: 'Home',
        icon: <DashboardIcon />,
        relativePath: '/positions',
        children: [
          {
            id: 'positions-predictions',
            name: 'My predictions',
            relativePath: '/predictions',
            icon: <LuTrendingUp size="18px" />,
            children: [],
            component: PredictionsPage,
            enableExplanation: true,
          },
        ],
        component: PositionsHomePage,
      },
      {
        id: 'supply',
        name: 'Supply',
        relativePath: '/supply',
        icon: <ExploreIcon />,
        children: [],
        component: SupplyChainsPage,
      },
      {
        id: 'explore',
        name: 'Hedge',
        icon: <ExploreIcon />,
        relativePath: '/explore',
        children: [],
        component: ExploreCommoditiesPage,
      },
      {
        id: 'predict',
        name: 'Predict',
        relativePath: '/prediction-markets',
        icon: <GlobeStandIcon />,
        children: [],
        component: PredictionMarketPage,
      },
      {
        id: 'procure',
        name: 'Procure',
        icon: <CreateIcon />,
        relativePath: '/procure',
        children: [],
        component: CreatePage,
        enableExplanation: true,
      },
      {
        id: 'cash-balance',
        name: 'Cash balance',
        icon: <WalletIcon />,
        relativePath: '/cash-balance',
        children: [],
        component: CashBalancePage,
        enableExplanation: true,
      },
    ],
  };

  cachedSidebarRoots = { personaModeNavigationRoots, publicSidebarNavigationRoot };
  return cachedSidebarRoots;
};

const settingsNavigationRoot: NavigationEntity = {
  id: 'root',
  name: 'Root',
  relativePath: '/settings',
  children: [
    {
      id: 'stores',
      name: 'Stores',
      relativePath: '/stores',
      icon: <PageIcon />,
      children: [],
    },
    {
      id: 'my-account',
      name: 'My account',
      relativePath: '/my-account',
      icon: <PageIcon />,
      children: [],
    },
    {
      id: 'preferences',
      name: 'Preferences',
      relativePath: '/preferences',
      icon: <PageIcon />,
      children: [],
    },
    {
      id: 'billing',
      name: 'Billing',
      relativePath: '/billing',
      icon: <PageIcon />,
      children: [],
    },
    {
      id: 'permissions',
      name: 'Roles & permissions',
      relativePath: '/permissions',
      icon: <PageIcon />,
      children: [],
    },
  ],
};
const filterHiddenEntities = (entities: NavigationEntity[]): NavigationEntity[] => {
  return entities
    .filter((entity) => entity.hide !== true)
    .map((entity) => ({
      ...entity,
      children: filterHiddenEntities(entity.children),
    }));
};

const flattenNavigationEntities = (
  entities: NavigationEntity[],
  parentPath: string,
): NavigationEntity[] => {
  return entities.flatMap((entity) => {
    let absolutePath =
      parentPath === '/' ? entity.relativePath : `${parentPath}${entity.relativePath}`;
    if (entity.relativePath === '/') {
      absolutePath = parentPath;
    }
    return [
      {
        ...entity,
        absolutePath,
      },
      ...flattenNavigationEntities(entity.children, absolutePath),
    ];
  });
};

export const isSelectedNavigationEntity = ({
  entity,
  selectedNavigationEntity,
}: {
  entity: NavigationEntity;
  selectedNavigationEntity?: NavigationEntity;
}) => {
  return entity.id === selectedNavigationEntity?.id;
};

export function useIsSelected(entity: NavigationEntity) {
  const { selectedNavigationEntity } = useSidebarNavigationEntities();
  return isSelectedNavigationEntity({ entity, selectedNavigationEntity });
}

const getSidebarNavigationRoot = ({
  orgSlug,
  personaMode,
}: {
  orgSlug: string | undefined;
  personaMode: PersonaMode;
}) => {
  const { personaModeNavigationRoots, publicSidebarNavigationRoot } = getSidebarRoots();
  if (orgSlug != null) {
    return personaModeNavigationRoots[personaMode] ?? personaModeNavigationRoots[DEFAULT_PERSONA_MODE];
  }
  return publicSidebarNavigationRoot;
};

export const useSidebarNavigationEntities = () => {
  const orgSlug = useOrgSlug();
  const personaMode = useActivePersonaMode();
  const basePath = orgSlug != null ? `/${orgSlug}/${personaMode}` : '';
  const sidebarNavigationRoot = getSidebarNavigationRoot({ orgSlug, personaMode });
  const entities = useNavigationEntities(sidebarNavigationRoot, basePath);
  const previousSelectedEntity = usePrevious(entities.selectedNavigationEntity);
  const { isOpen } = useSettingsNavigationEntities();

  return useMemo(
    () => ({
      ...entities,
      selectedNavigationEntity: isOpen ? previousSelectedEntity : entities.selectedNavigationEntity,
    }),
    [entities, previousSelectedEntity, isOpen],
  );
};

export const useSettingsNavigationEntities = () => {
  const orgSlug = useOrgSlug();
  const basePath = orgSlug != null ? `/${orgSlug}/settings` : '/settings';
  const entities = useNavigationEntities(settingsNavigationRoot, basePath);
  const pathname = usePathname();
  const isOpen = pathname.startsWith(basePath);
  const { pageStore } = useStores();
  const router = useRouter();

  const closeSettings = useCallback(() => {
    if (pageStore.sidebarPath != null) {
      router.replace(pageStore.sidebarPath);
    } else {
      router.replace(`/${orgSlug}`);
    }
  }, [pageStore.sidebarPath, router, orgSlug]);

  return useMemo(
    () => ({
      ...entities,
      basePath,
      isOpen,
      closeSettings,
    }),
    [entities, basePath, isOpen, closeSettings],
  );
};

const useNavigationEntities = (root: NavigationEntity, basePath: string) => {
  const pathname = usePathname();

  const visibleRoot = useMemo(
    () => ({
      ...root,
      children: filterHiddenEntities(root.children),
    }),
    [root],
  );

  const flattenedEntities = useMemo(
    () => flattenNavigationEntities(visibleRoot.children, basePath),
    [visibleRoot, basePath],
  );

  const selectedNavigationEntity = useMemo(() => {
    const matchingEntities = flattenedEntities.filter((entity) => {
      return entity.absolutePath != null && pathname.startsWith(entity.absolutePath);
    });
    // sorted by length of absolute path in descending order.
    // The assumption here is that the longest absolute path is the most specific.
    const sortedEntities = matchingEntities.sort(
      (a, b) => (b.absolutePath?.length ?? 0) - (a.absolutePath?.length ?? 0),
    );
    return sortedEntities[0];
  }, [flattenedEntities, pathname]);

  return {
    root: visibleRoot,
    all: flattenedEntities,
    byId: keyBy(flattenedEntities, 'id'),
    selectedNavigationEntity,
  };
};
